import { parseDateTimeString } from './dates';
import { listingTimeZone as listingTimeZoneName } from './timeZone';
import { isTalkSessionTransaction } from './sessionPricing';

/** Product calendar day for Today / Past chips. */
export const MARKETPLACE_DAY_TZ = 'America/New_York';

const SESSION_LENGTH_MS = 15 * 60 * 1000;

/** Server no-show cancel is start + 4 minutes. Client fallback uses the same. */
export const LISTENER_NO_SHOW_GRACE_MS = 4 * 60 * 1000;

/**
 * Coerce SDK dates, ISO strings, and epoch ms to a Date.
 *
 * @param {*} value
 * @returns {Date|null}
 */
export const instantFrom = value => {
  if (!value && value !== 0) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const ms = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(ms) ? new Date(ms) : null;
};

const bookingRecord = tx => tx?.booking?.attributes || tx?.booking || {};

const listingTimeZone = tx =>
  listingTimeZoneName(tx?.listing?.attributes?.availabilityPlan?.timezone, MARKETPLACE_DAY_TZ);

/**
 * Sharetribe `displayStart` / `displayEnd` are listing-local clock faces stuffed
 * into UTC (15:15Z means 3:15 PM on the listing clock, not 15:15 UTC).
 * Rebuild that clock in `timeZone` so Inbox/Dashboard compare the wall time
 * Sam sees (America/New_York for this marketplace).
 *
 * @param {*} value
 * @param {string} [timeZone]
 * @returns {Date|null}
 */
export const displayClockToInstant = (value, timeZone = MARKETPLACE_DAY_TZ) => {
  const date = instantFrom(value);
  if (!date) {
    return null;
  }
  const y = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${date.getUTCDate()}`.padStart(2, '0');
  const hour = `${date.getUTCHours()}`.padStart(2, '0');
  const minute = `${date.getUTCMinutes()}`.padStart(2, '0');
  const parsed = parseDateTimeString(`${y}-${month}-${day} ${hour}:${minute}`, timeZone);
  return instantFrom(parsed) || date;
};

/**
 * Wall-clock bounds for Upcoming vs In session vs Past.
 * True UTC `start`/`end` win. Display clocks are listing-local faces.
 *
 * @param {Object} [tx]
 * @returns {{ start: Date|null, end: Date|null }}
 */
export const bookingTimes = tx => {
  const booking = bookingRecord(tx);
  const startNow = tx?.attributes?.protectedData?.startNow === true;
  const zone = listingTimeZone(tx);
  const bookedStart =
    instantFrom(booking.start) ||
    displayClockToInstant(booking.displayStart, zone) ||
    instantFrom(booking.displayStart);
  const startNowAnchor = startNow
    ? instantFrom(tx?.attributes?.createdAt) || instantFrom(tx?.attributes?.lastTransitionedAt)
    : null;
  const startCandidates = [bookedStart, startNowAnchor].filter(Boolean);
  const start =
    startCandidates.length > 0
      ? new Date(Math.min(...startCandidates.map(date => date.getTime())))
      : null;
  const rawEnd =
    instantFrom(booking.end) ||
    displayClockToInstant(booking.displayEnd, zone) ||
    instantFrom(booking.displayEnd);
  const end = rawEnd || (start ? new Date(start.getTime() + SESSION_LENGTH_MS) : null);
  return { start, end };
};

/**
 * Exclusive booking end instant used for Past vs Upcoming.
 *
 * @param {Object} [tx]
 * @returns {Date|null}
 */
export const bookingEndOf = tx => bookingTimes(tx).end;

const WEEKDAY_SUN0 = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const pad2 = n => `${n}`.padStart(2, '0');

/**
 * YYYY-MM-DD for an instant on the America/New_York calendar.
 *
 * @param {Date} date
 * @returns {string|null}
 */
export const nyDateKey = date => {
  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString('en-CA', {
    timeZone: MARKETPLACE_DAY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * @param {string} key YYYY-MM-DD
 * @returns {{ year: number, month: number, day: number }}
 */
export const ymdFromNyDateKey = key => {
  const [year, month, day] = String(key || '')
    .split('-')
    .map(Number);
  return { year, month, day };
};

/**
 * NY calendar parts for an instant (defaults to now).
 *
 * @param {Date} [date]
 * @returns {{ year: number, month: number, day: number, key: string|null }}
 */
export const nyPartsFromDate = (date = new Date()) => {
  const key = nyDateKey(date);
  return { ...ymdFromNyDateKey(key), key };
};

/**
 * @param {number} year
 * @param {number} month 1-12
 * @param {number} day
 * @returns {string}
 */
export const nyDateKeyFromYmd = (year, month, day) => `${year}-${pad2(month)}-${pad2(day)}`;

const daysInCalendarMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * Shift a year/month pair by `delta` months.
 *
 * @param {number} year
 * @param {number} month 1-12
 * @param {number} delta
 * @returns {{ year: number, month: number }}
 */
export const shiftYearMonth = (year, month, delta) => {
  const shifted = new Date(Date.UTC(year, month - 1 + delta, 1));
  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1 };
};

const weekdaySun0ForNyYmd = (year, month, day) => {
  const instant = parseDateTimeString(
    `${nyDateKeyFromYmd(year, month, day)} 12:00`,
    MARKETPLACE_DAY_TZ
  );
  const label = instant.toLocaleDateString('en-US', {
    weekday: 'short',
    timeZone: MARKETPLACE_DAY_TZ,
  });
  return WEEKDAY_SUN0[label] ?? 0;
};

/**
 * Sunday-start month grid in America/New_York (leading/trailing days included).
 *
 * @param {number} year
 * @param {number} month 1-12
 * @returns {Array<{ key: string, year: number, month: number, day: number, inMonth: boolean, weekday: number }>}
 */
export const monthGridCells = (year, month) => {
  const dim = daysInCalendarMonth(year, month);
  const lead = weekdaySun0ForNyYmd(year, month, 1);
  const prev = shiftYearMonth(year, month, -1);
  const prevDim = daysInCalendarMonth(prev.year, prev.month);
  const cells = [];

  for (let i = 0; i < lead; i++) {
    const day = prevDim - lead + 1 + i;
    cells.push({
      key: nyDateKeyFromYmd(prev.year, prev.month, day),
      year: prev.year,
      month: prev.month,
      day,
      inMonth: false,
      weekday: i,
    });
  }

  for (let day = 1; day <= dim; day++) {
    cells.push({
      key: nyDateKeyFromYmd(year, month, day),
      year,
      month,
      day,
      inMonth: true,
      weekday: (lead + day - 1) % 7,
    });
  }

  const next = shiftYearMonth(year, month, 1);
  let nextDay = 1;
  while (cells.length % 7 !== 0) {
    cells.push({
      key: nyDateKeyFromYmd(next.year, next.month, nextDay),
      year: next.year,
      month: next.month,
      day: nextDay,
      inMonth: false,
      weekday: cells.length % 7,
    });
    nextDay += 1;
  }

  return cells;
};

/**
 * True when the booked slot is already over. Compares the booking **end**
 * instant to now — not start-only, not a UTC calendar-day cutoff.
 * Display-only clocks are read as America/New_York wall time so a Sep 19
 * 3:15 PM row cannot stay Upcoming on Sep 20.
 *
 * @param {Object} [tx]
 * @param {Date} [now]
 * @returns {boolean}
 */
export const bookingHasEnded = (tx, now = new Date()) => {
  const nowMs = now.getTime();
  const { end } = bookingTimes(tx);
  if (end && end.getTime() <= nowMs) {
    return true;
  }

  const booking = bookingRecord(tx);
  const displayEnd = booking.displayEnd || booking.end;
  const nyEnd = displayClockToInstant(displayEnd, MARKETPLACE_DAY_TZ);
  if (nyEnd && nyEnd.getTime() <= nowMs) {
    return true;
  }

  // Belt: NY calendar day of the end is already before today's NY date.
  const endForDay = end || nyEnd;
  return !!(endForDay && nyDateKey(endForDay) < nyDateKey(now));
};

const PENDING_ACCEPT_STATES = new Set(['preauthorized', 'pending-payment', 'expired']);

/**
 * Booking never accepted and the America/New_York session end is already past
 * (or the process already expired the request). Inbox / dashboard show
 * “No confirmation” instead of a stale waiting-for-review chip.
 *
 * @param {Object} [tx]
 * @param {string} [processState]
 * @param {Date} [now]
 * @returns {boolean}
 */
export const isUnconfirmedPastBooking = (tx, processState, now = new Date()) => {
  if (!PENDING_ACCEPT_STATES.has(processState)) {
    return false;
  }
  if (processState === 'expired') {
    return true;
  }
  return bookingHasEnded(tx, now);
};

/**
 * True when the talker join was persisted on the transaction.
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const txTalkerHasJoined = tx => {
  const data = tx?.attributes?.protectedData || {};
  const meta = tx?.attributes?.metadata || {};
  return !!(data.talkerJoinedAt || meta.talkerJoinedAt || data.bothJoinedAt || meta.bothJoinedAt);
};

/**
 * True when the listener join was persisted on the transaction.
 * Talker join / page load must not count.
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const txListenerHasJoined = tx => {
  const data = tx?.attributes?.protectedData || {};
  const meta = tx?.attributes?.metadata || {};
  return !!(
    data.listenerJoinedAt ||
    meta.listenerJoinedAt ||
    data.bothJoinedAt ||
    meta.bothJoinedAt
  );
};

const NO_SHOW_DISPLAY_TRANSITIONS = new Set([
  'transition/accept',
  'transition/operator-accept',
]);

/**
 * Past start + 4 minutes with no listener join. Inbox / dashboard must not
 * keep this as In session, Join, or Upcoming while Flex is still accepted.
 * Completed / charged / cancelled transitions are left alone.
 *
 * @param {Object} [tx]
 * @param {Date} [now]
 * @returns {boolean}
 */
export const txPastListenerNoShowDeadline = (tx, now = new Date()) => {
  if (!tx || txListenerHasJoined(tx) || !isTalkSessionTransaction(tx)) {
    return false;
  }
  const last = tx?.attributes?.lastTransition;
  const startNow = tx?.attributes?.protectedData?.startNow === true;
  const stillAccepted =
    NO_SHOW_DISPLAY_TRANSITIONS.has(last) ||
    (startNow && (last === 'transition/confirm-payment' || last === 'transition/request-payment'));
  if (!stillAccepted) {
    return false;
  }
  const start = bookingTimes(tx).start;
  if (!start) {
    return false;
  }
  return now.getTime() >= start.getTime() + LISTENER_NO_SHOW_GRACE_MS;
};

/**
 * Past start + 4 minutes, listener joined, talker never joined.
 *
 * @param {Object} [tx]
 * @param {Date} [now]
 * @returns {boolean}
 */
export const txPastTalkerNoShowDeadline = (tx, now = new Date()) => {
  if (!tx || !txListenerHasJoined(tx) || txTalkerHasJoined(tx) || !isTalkSessionTransaction(tx)) {
    return false;
  }
  const last = tx?.attributes?.lastTransition;
  if (!NO_SHOW_DISPLAY_TRANSITIONS.has(last)) {
    return false;
  }
  const start = bookingTimes(tx).start;
  if (!start) {
    return false;
  }
  return now.getTime() >= start.getTime() + LISTENER_NO_SHOW_GRACE_MS;
};

export const txPastNoShowDeadline = (tx, now = new Date()) =>
  txPastListenerNoShowDeadline(tx, now) || txPastTalkerNoShowDeadline(tx, now);

/**
 * Accepted slot that has started and not yet ended.
 *
 * @param {Object} [tx]
 * @param {Date} [now]
 * @returns {boolean}
 */
export const bookingIsInSession = (tx, now = new Date()) => {
  if (
    bookingHasEnded(tx, now) ||
    txPastNoShowDeadline(tx, now)
  ) {
    return false;
  }
  return bookingIsInLiveWindow(tx, now);
};

/**
 * Booked start has passed and the booked end has not. Used for My Bookings
 * “In session now” so a live slot is never labeled Next session just because
 * Flex has not stored join timestamps yet.
 *
 * @param {Object} [tx]
 * @param {Date} [now]
 * @returns {boolean}
 */
export const bookingIsInLiveWindow = (tx, now = new Date()) => {
  if (!tx || bookingHasEnded(tx, now)) {
    return false;
  }
  const { start, end } = bookingTimes(tx);
  const nowMs = now.getTime();
  return !!(start && end && nowMs >= start.getTime() && nowMs <= end.getTime());
};

/**
 * Today’s Schedule: live now, or starting on the New York calendar day.
 * Upcoming future days stay off this card.
 *
 * @param {Object} [info] classifySession result
 * @returns {boolean}
 */
export const isOnTodaySchedule = info =>
  !!(info?.isLive || (info?.isToday && !info?.isPast && !info?.canceled));

/**
 * Future-day Upcoming list: accepted and not started, not today’s NY calendar day.
 *
 * @param {Object} [info] classifySession result
 * @returns {boolean}
 */
export const isUpcomingListRow = info => !!(info?.isUpcoming && !info?.isToday);
